import React, { useEffect, useRef } from "react";
import { Spin } from "antd";
import { useTranslation } from "react-i18next";
import { useReactToPrint } from "react-to-print";
import AppButton from "components/ui/AppButton";
import { getOrderIsRefundable } from "utils/Order";
import OrderDetailUnifiedBody from "../components/OrderDetailUnifiedBody";

const ShowOrderDetail = ({
  rowData,
  loading,
  showRefundModal,
  onPrintReady,
}) => {
  const componentRef = useRef();
  const { t } = useTranslation();
  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
  });

  useEffect(() => {
    onPrintReady?.(handlePrint);
    return () => onPrintReady?.(null);
  }, [handlePrint, onPrintReady]);

  const getStatusText = () => {
    if (!rowData?.orderStatus) return "N/A";
    const status = rowData.orderStatus.replaceAll("_", " ");
    return status
      .toLowerCase()
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const getStatusBadgeStyle = () => {
    const rawStatus = String(rowData?.orderStatus || "");
    const normalizedStatus = rawStatus
      .toUpperCase()
      .replaceAll(" ", "_")
      .replaceAll("/", "_");

    const statusStyles = {
      PENDING: { backgroundColor: "#FFF9E6", color: "#FF8C00" },
      PREPARING: { backgroundColor: "#FFF4E6", color: "#F97316" },
      ON_THE_WAY: { backgroundColor: "#E6F2FF", color: "#3B82F6" },
      READY: { backgroundColor: "#E6F2FF", color: "#3B82F6" },
      COMPLETE: { backgroundColor: "#E6F7E6", color: "#22C55E" },
      COMPLETED: { backgroundColor: "#E6F7E6", color: "#22C55E" },
      SCHEDULED: { backgroundColor: "#E6F7E6", color: "#22C55E" },
      REJECTED: { backgroundColor: "#FFE6E6", color: "#EF4444" },
      CANCELLED: { backgroundColor: "#FFE6E6", color: "#EF4444" },
      CANCELED: { backgroundColor: "#FFE6E6", color: "#EF4444" },
      REFUND: { backgroundColor: "#FDECEC", color: "#D4380D" },
      REFUNDED: { backgroundColor: "#FDECEC", color: "#D4380D" },
    };

    return (
      statusStyles[normalizedStatus] || {
        backgroundColor: "#F3F4F6",
        color: "#4B5563",
      }
    );
  };

  return (
    <Spin spinning={loading}>
      <div className="w-full relative">
        <div className="sticky top-0 bg-white mb-4 pb-3" style={{ zIndex: 5 }}>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <span className="text-2xl font-semibold text-gray-900">
              {t("Order")} # {rowData?.orderNumber || "N/A"}
            </span>
            {rowData?.orderStatus ? (
              <span
                className="rounded-full px-3 py-1 text-sm font-medium"
                style={getStatusBadgeStyle()}
              >
                {getStatusText()}
              </span>
            ) : null}
          </div>
          <div className="mt-3 flex items-center justify-end gap-2">
            {
            (rowData?.refund?.status === "FAILED" || (Boolean(
              (!rowData?.refund ||
                !rowData?.refund?.status ||
                !rowData?.refund?.amount ||
                rowData?.refund?.amount === 0) &&
                getOrderIsRefundable(rowData)
            )) && (
              <AppButton
                type="button"
                variant="danger"
                size="sm"
                className="hidden-print"
                onClick={() => showRefundModal?.()}
              >
                {t("Refund")}
              </AppButton>
            ))}
          </div>
        </div>

        <OrderDetailUnifiedBody
          order={rowData}
          orderId={rowData?._id}
          tabStatus={rowData?.orderStatus}
          getOrderInfoById={() => {}}
          showToolbar={false}
          showDeliveryActions={false}
          printContentRef={componentRef}
        />
      </div>
    </Spin>
  );
};

export default ShowOrderDetail;
